import { useTranslation } from '@/contexts/i18n-context';
import { PageContainer } from '@/components/containers/page-container';
import { Section } from '@/components/containers/section';
import { TextInput } from '@/components/inputs/text-input';
import { Button } from '@/components/button';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/form';
import { Checkbox } from '@/components/inputs/checkbox';
import { termsOfUseBlueFinLink } from '@/util/urls';
import { useEffect, useRef, useState } from 'react';
import styles from './default-payment-page.module.css';
import { useEncounter } from '@/contexts/encounter-context';
import { useCreateEncountersPayments } from '@/api/create-payment';
import { Navigate, useNavigate } from '@/util/react-router-dom';
import { useCreateEncountersPromocode } from '@/api/apply-promo-code';
import {
  ApplyPromoCodeErrorType,
  CreatePaymentErrorType,
  GetEncounterPriceResponse,
} from '@/api/api.schemas';
import { useDeleteEncountersPromocode } from '@/api/delete-promo-code-from-encounter';
import { useGetEncountersPrice } from '@/api/get-encounter-price';
import {
  AdditionalAddressProps,
  PaymentIframe,
  usePaymentIframe,
} from '@/components/payment/payment-iframe';
import { Banner } from '@/components/banner';
import { TranslationKey } from '@/contexts/translations';
import { BluefinInvalidInputError, EncryptResponse } from '@/types/payconex';
import {
  getErrorMessages,
  getUserFriendlyErrorMessage,
} from '@/components/payment/payment-error-handle';
import { Loader } from '@/components/loaders/loader';
import { useMediaQuery } from '@/hooks/use-media-query';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import clsx from 'clsx';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import { FormButtonRow } from '@/components/containers/form-button-row';
import { useBranding } from '@/outlets/branding-outlet';
import { InsuranceCoverageModal } from '@/components/encounter/insurance/insurance-coverage-modal';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { formatPrice } from '@/util/formats';
import { CardIconsContainer } from '../card-icons-container';
import { StateSelector } from '@/components/form-controls/state-selector';
import type { KeyboardEvent } from 'react';
import {
  requiredAddress,
  requiredState,
  requiredString,
  requiredZipCode,
  zt,
} from '@/util/validators';
import { useAsyncAction } from '@/hooks/use-async-action';

const translationBase = 'visit.payment';
const translationBasePromoCode = `${translationBase}.promoCode`;
const translationBaseIFrameError = `${translationBase}.iFrameErrors`;

const FreeVisitSchema = z.object({
  isFreeVisit: z.literal(true),
  promoCode: z.string().optional(),
});

const PaidVisitSchema = z.object({
  isFreeVisit: z.literal(false),
  promoCode: z.string().optional(),
  address: requiredAddress(),
  city: requiredString(),
  state: requiredState(),
  zipCode: requiredZipCode(),
  termsOfUse: z.boolean().refine((val) => val === true, {
    message: zt(`${translationBase}.termsOfUse.error`),
  }),
});

const PaymentFormSchema = z.discriminatedUnion('isFreeVisit', [FreeVisitSchema, PaidVisitSchema]);

type FormValues = z.infer<typeof PaymentFormSchema>;

export function DefaultPaymentPage() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const {
    data: encounterPrice,
    error: encounterPriceError,
    isPending,
    isFetching,
    refetch: refetchPrice,
  } = useGetEncountersPrice(encounter.id);

  if (isPending) {
    return <Loader type="large" />;
  }

  if (encounterPriceError) {
    return (
      <PageContainer>
        <GradientFormTitle>
          <h1 className="h3">{t(`${translationBase}.title`)}</h1>
        </GradientFormTitle>
        <Section>
          <Banner type="error">{t(`${translationBase}.priceError`)}</Banner>
        </Section>
      </PageContainer>
    );
  }

  if (!encounterPrice) {
    return <Navigate to="/system-error" />;
  }

  const isFreeVisit = encounterPrice.adjustedAmount === 0;

  return (
    <DefaultPaymentForm
      isFreeVisit={isFreeVisit}
      encounterPrice={encounterPrice}
      isFetching={isFetching}
      refetchPrice={refetchPrice}
    />
  );
}

interface CreatePaymentProps {
  response: EncryptResponse;
  addressInfo: AdditionalAddressProps;
}

interface DefaultPaymentFormProps {
  isFreeVisit: boolean;
  encounterPrice: GetEncounterPriceResponse;
  isFetching: boolean;
  refetchPrice: () => void;
}

export function DefaultPaymentForm({
  isFreeVisit,
  encounterPrice,
  isFetching,
  refetchPrice,
}: DefaultPaymentFormProps) {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const { hasInsurance, promoCodeEnabled, displayOncePerWeekInsuranceDisclaimer } = useBranding();
  const [isPromoCodeOpen, setIsPromoCodeOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery('--screen-md');

  const [paymentError, setPaymentError] = useState<string>();
  const [paymentIFrameError, setPaymentIFrameError] = useState<TranslationKey[]>([]);
  const paymentErrorRef = useRef<HTMLDivElement>(null);
  const paymentIFrameErrorRef = useRef<HTMLDivElement>(null);

  const { mutate: applyPromoCode, isPending: isApplyingPromoCode } = useCreateEncountersPromocode();
  const { mutate: deletePromoCode, isPending: isDeletingPromoCode } =
    useDeleteEncountersPromocode();
  const { mutate: payForEncounter, isPending: isPaymentProcessing } = useCreateEncountersPayments();

  const defaultValues = isFreeVisit
    ? {
        isFreeVisit,
        promoCode: encounterPrice.promoCode ?? '',
      }
    : {
        isFreeVisit,
        termsOfUse: false,
        address: '',
        city: '',
        state: '',
        zipCode: '',
      };

  const form = useForm<FormValues>({
    resolver: zodResolver(PaymentFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const { isProcessing, requestEncryption } = usePaymentIframe({
    onFailure: () => {
      setPaymentIFrameError([`${translationBaseIFrameError}.failure`]);
      scrollToError(paymentIFrameErrorRef);
    },
    onInvalidInput: (error: BluefinInvalidInputError) => {
      setPaymentIFrameError(getErrorMessages(error));
      scrollToError(paymentIFrameErrorRef);
    },
  });

  const createPayment = (paymentProps?: CreatePaymentProps) => {
    const data = paymentProps
      ? {
          isFreeVisit: false,
          paymentToken: paymentProps.response.eToken,
          ...paymentProps.addressInfo,
        }
      : { isFreeVisit: true };

    payForEncounter(
      {
        encounterId: encounter.id,
        data,
      },
      {
        onSuccess: (data) => {
          navigate(`/visit/${encounter.id}/payment/success`, {
            state: data,
          });
        },
        onError: (error) => {
          let errorKey: TranslationKey = `${translationBase}.failure`;
          switch (error.type) {
            case CreatePaymentErrorType.PromoCodeInvalid:
              errorKey = `${translationBase}.promoCodeInvalid`;
              form.setValue('promoCode', '');
              refetchPrice();
              break;
            case CreatePaymentErrorType.PaymentFailed:
            case CreatePaymentErrorType.PaymentServerError:
              errorKey = getUserFriendlyErrorMessage(error.errorCode);
              break;
            case CreatePaymentErrorType.AlreadySubmitted:
              errorKey = `${translationBase}.alreadySubmitted`;
              break;
            case CreatePaymentErrorType.InvalidVisitStatus:
              errorKey = `${translationBase}.invalidVisitStatus`;
              break;
          }
          setPaymentError(t(errorKey));
          scrollToError(paymentErrorRef);
        },
      },
    );
  };

  const onSubmitPromoCode = () => {
    const promoCode = form.getValues('promoCode');

    if (!promoCode?.trim()) {
      form.setError('promoCode', { message: t('common.errors.required') });
      return;
    }

    applyPromoCode(
      { encounterId: encounter.id, data: { promoCode: promoCode ?? '' } },
      {
        onSuccess: () => {
          refetchPrice();
        },
        onError: (error) => {
          if (error.type === ApplyPromoCodeErrorType.NotFound) {
            form.setError('promoCode', {
              message: t(`${translationBasePromoCode}.invalid`),
            });
            return;
          }

          form.setError('promoCode', { message: t('common.errors.unknown') });
        },
      },
    );
  };

  const onRemove = () => {
    deletePromoCode(
      { encounterId: encounter.id },
      {
        onSuccess: () => {
          form.setValue('promoCode', '');
          refetchPrice();
        },
      },
    );
  };

  const { trigger: handleNext } = useAsyncAction(async (data: FormValues) => {
    setPaymentError(undefined);
    setPaymentIFrameError([]);

    if (data.isFreeVisit) {
      createPayment();
      return;
    }

    const { response, additionalProps } = await requestEncryption({
      address: data.address,
      zipCode: data.zipCode,
      city: data.city,
      state: data.state,
    });

    createPayment({ response, addressInfo: additionalProps });
  });

  const scrollToError = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      });
      ref.current.focus();
    }
  };

  useEffect(() => {
    if (isFreeVisit) {
      setPaymentError(undefined);
    }

    form.setValue('isFreeVisit', isFreeVisit);
  }, [isFreeVisit, form]);

  function inputOnKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      e.currentTarget.blur(); // Close keyboard on mobile
    }
  }

  const showPriceBreakdown = !!encounterPrice.adjustment || hasInsurance;

  return (
    <PageContainer>
      <Form form={form} onSubmit={handleNext}>
        {!isMobile && <VisitTitleButtons />}
        <GradientFormTitle>
          {isMobile && <VisitTitleButtons />}
          <VisitNavigation />
          <h1 className="h3">{t(`${translationBase}.title`)}</h1>
          <p>{t(`${translationBase}.description`)}</p>
          {isMobile && <SaveProgressButton />}
        </GradientFormTitle>

        <Section className={styles.paymentContainer}>
          <div ref={paymentErrorRef}>
            {paymentError && <Banner type="error">{paymentError}</Banner>}
          </div>

          <div className={styles.priceContainer}>
            <div>
              <h2 className={clsx('overline', styles.sectionTitle)}>
                {t(`${translationBase}.sectionTitle`)}
              </h2>
              <div className={styles.divider} />
            </div>
            {hasInsurance && <Banner type="info">{t(`${translationBase}.costDisclaimer`)}</Banner>}
            <div className={styles.costContainer}>
              {isFetching ? (
                <Loader type="small" />
              ) : (
                <>
                  {showPriceBreakdown && (
                    <div className={styles.adjustmentContainer}>
                      <div className={styles.row}>
                        <span>{t(`${translationBase}.standardPrice`)}</span>
                        <span className="body-3">{formatPrice(encounterPrice.baseAmount)}</span>
                      </div>
                      <div className={styles.row}>
                        {hasInsurance ? (
                          <div className={styles.insuranceCoverage}>
                            <span className={styles.insuranceCoverageSpan}>
                              {t(`${translationBase}.insuranceCoverage`)}
                              <MaterialSymbolIcon
                                name="info"
                                className={styles.infoIcon}
                                ariaLabel={`${translationBase}.insuranceCoverage.infoLabel`}
                                onClick={() => setIsOpen(true)}
                              />
                            </span>
                            {displayOncePerWeekInsuranceDisclaimer &&
                              encounterPrice.hasVisitsWithinSevenDays && (
                                <span
                                  className={clsx(
                                    'body-3',
                                    'semibold',
                                    styles.oncePerWeekDisclaimer,
                                  )}
                                >
                                  {t(`${translationBase}.subsequentVisitDisclaimer`)}
                                </span>
                              )}
                          </div>
                        ) : (
                          <span>{t(`${translationBase}.promoCodeDiscount`)}</span>
                        )}
                        <span className="body-3">{formatPrice(encounterPrice.adjustment)}</span>
                      </div>
                      <div className={styles.divider} />
                    </div>
                  )}
                  {encounterPrice?.adjustedAmount !== undefined && (
                    <div className={clsx('body-1', 'bold', styles.row)}>
                      <span>{t(`${translationBase}.yourCost`)}</span>
                      <span>{formatPrice(encounterPrice.adjustedAmount)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            {promoCodeEnabled && (
              <div className={styles.promoCodeContainer}>
                <div className={styles.promCodeBtn}>
                  <MaterialSymbolIcon name="confirmation_number" className={styles.icon} />
                  <Button
                    variant="link"
                    rightIcon={isPromoCodeOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                    className={styles.button}
                    onClick={() => setIsPromoCodeOpen(!isPromoCodeOpen)}
                  >
                    {t(`${translationBasePromoCode}.title`)}
                  </Button>
                </div>
                {isPromoCodeOpen && (
                  <div className={styles.formContainer}>
                    <TextInput
                      name="promoCode"
                      placeholder={t(`${translationBasePromoCode}.placeholder`)}
                      withIconsIndicators={false}
                      disabled={!!encounterPrice?.promoCode}
                    />
                    {encounterPrice?.adjustment ? (
                      <Button
                        variant="outlined"
                        className={clsx(styles.button, styles.removeBtn)}
                        onClick={onRemove}
                        loading={isDeletingPromoCode}
                      >
                        {t(
                          `${translationBasePromoCode}.${isMobile ? 'removeBtnMobile' : 'removeBtn'}`,
                        )}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outlined"
                        className={styles.button}
                        onClick={onSubmitPromoCode}
                        loading={isApplyingPromoCode}
                      >
                        {t(
                          `${translationBasePromoCode}.${isMobile ? 'applyBtnMobile' : 'applyBtn'}`,
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <div className={clsx(styles.titleSection, isFreeVisit && styles.disabled)}>
              <div className={styles.paymentInformation}>
                <span className="semibold">{t(`${translationBase}.payViaCard`)}</span>
                <span className={clsx('body-3', styles.subText)}>
                  {t(`${translationBase}.payViaHsaCard`)}
                </span>
              </div>
              <CardIconsContainer />
            </div>
            <div className={clsx(styles.iframeContainer, isFreeVisit && styles.hidden)}>
              <div className={styles.bannerContainer}>
                <Banner type="lock">
                  <span>{t(`${translationBase}.lockBanner`)}</span>
                </Banner>

                <div ref={paymentIFrameErrorRef}>
                  {paymentIFrameError.length > 0 && (
                    <Banner type="error">
                      <div className={styles.errorContainer}>
                        {paymentIFrameError.map((error) => (
                          <span key={error}>{t(error)}</span>
                        ))}
                      </div>
                    </Banner>
                  )}
                </div>
              </div>
              <PaymentIframe />
              <div className={styles.formInputs}>
                <TextInput
                  name="address"
                  label={t(`${translationBase}.billing.address.label`)}
                  placeholder={t(`${translationBase}.billing.address.placeholder`)}
                  description={t(`${translationBase}.billing.address.description`)}
                  className={styles.fullRow}
                  withIconsIndicators={false}
                  onKeyDown={inputOnKeyDown}
                />

                <TextInput
                  name="city"
                  label={t(`${translationBase}.billing.city.label`)}
                  placeholder={t(`${translationBase}.billing.city.placeholder`)}
                  withIconsIndicators={false}
                  enterKeyHint="next"
                  onKeyDown={inputOnKeyDown}
                />
                <StateSelector
                  name="state"
                  label={t(`${translationBase}.billing.state.label`)}
                  placeholder={t(`${translationBase}.billing.state.placeholder`)}
                  enterKeyHint="next"
                />
                <TextInput
                  name="zipCode"
                  label={t(`${translationBase}.billing.zipCode.label`)}
                  placeholder={t(`${translationBase}.billing.zipCode.placeholder`)}
                  withIconsIndicators={false}
                  enterKeyHint="next"
                  onKeyDown={inputOnKeyDown}
                />
              </div>
            </div>
          </div>
          {!isFreeVisit && (
            <span className={styles.support}>
              {t(`${translationBase}.support.text`, {
                link: (
                  <Button
                    className={styles.termsOfUseBtn}
                    variant="link"
                    as="a"
                    to="/support"
                    key="contact-support-link"
                  >
                    {t(`${translationBase}.support.link`)}
                  </Button>
                ),
              })}
            </span>
          )}
        </Section>

        {!isFreeVisit && (
          <div className={styles.checkBoxContainer}>
            <Checkbox
              name="termsOfUse"
              label={
                <span>
                  {t(`${translationBase}.termsOfUse.text`, {
                    link: (
                      <Button
                        className={styles.termsOfUseBtn}
                        target="_blank"
                        variant="link"
                        as="a"
                        to={termsOfUseBlueFinLink}
                        key="notice-of-privacy-link"
                      >
                        {t(`${translationBase}.termsOfUse.link`)}
                      </Button>
                    ),
                  })}
                </span>
              }
            />
          </div>
        )}

        <InsuranceCoverageModal isOpen={isOpen} onClose={() => setIsOpen(false)} />

        <FormButtonRow
          loading={isPaymentProcessing || isProcessing}
          disabled={isPaymentProcessing || isProcessing}
          buttonTextKey={`${translationBase}.${isFreeVisit ? 'payFreeBtn' : 'payBtn'}`}
          type="submit"
        />
      </Form>
    </PageContainer>
  );
}
